import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { SquaresIcon, CheckIcon, DocumentIcon } from '../ui/icons';
import { useSettingsStore } from '../../store/settings';

// Pattern to detect attached workflow note in user messages
const ATTACHMENT_PATTERN = /\[Attached workflow: (.+?) \((\d+) nodes?\)\]/;

interface MessageBubbleProps {
  role: 'user' | 'assistant';
  content: string;
}

export function MessageBubble({ role, content }: MessageBubbleProps) {
  const isUser = role === 'user';
  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Check if user message is an answer to questions
  const isAnswerMessage = isUser && content.startsWith('**Answers to Questions**');

  // Check if user message has an attachment note
  const attachmentMatch = isUser ? content.match(ATTACHMENT_PATTERN) : null;
  const attachmentInfo = attachmentMatch && attachmentMatch[1] && attachmentMatch[2]
    ? { name: attachmentMatch[1], nodeCount: parseInt(attachmentMatch[2], 10) }
    : null;

  // Remove attachment note from displayed content
  const displayContent = attachmentInfo
    ? content.replace(ATTACHMENT_PATTERN, '').trim()
    : content;

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex flex-col max-w-[85%]">
        <div
          className={`rounded-2xl px-4 py-3 ${
            isUser
              ? isAnswerMessage
                ? 'bg-primary-50 border border-primary-200 text-gray-700'
                : 'bg-gradient-to-br from-primary-600 to-primary-700 text-white shadow-sm'
              : 'border border-gray-200 bg-gray-50 text-gray-800'
          }`}
        >
          {/* Attachment chip for user messages */}
          {attachmentInfo && (
            <div className="mb-2 pb-2 border-b border-white/20">
              <div className="inline-flex items-center gap-1.5 rounded-md bg-white/20 px-2 py-1 text-xs">
                <DocumentIcon className="h-3.5 w-3.5" />
                <span className="font-medium">{attachmentInfo.name}</span>
                <span className="opacity-80">({attachmentInfo.nodeCount} nodes)</span>
              </div>
            </div>
          )}
          <div
            className={`prose max-w-none text-[15px] ${isUser && !isAnswerMessage ? 'prose-invert prose-p:text-white prose-headings:text-white prose-strong:text-white prose-code:text-white prose-li:text-white' : ''}`}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              components={{
                a: ({ href, children, ...props }) => {
                  if (href === '#upgrade') {
                    return (
                      <a
                        href="#upgrade"
                        onClick={(e) => {
                          e.preventDefault();
                          useSettingsStore.getState().openUpgradeModal();
                        }}
                        className="text-primary-600 underline hover:text-primary-800 cursor-pointer"
                        {...props}
                      >
                        {children}
                      </a>
                    );
                  }
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                      {children}
                    </a>
                  );
                },
              }}
            >
              {displayContent}
            </ReactMarkdown>
          </div>
        </div>

        <button
          onClick={handleCopy}
          className={`self-start mt-1 p-1 rounded transition-opacity duration-200 hover:bg-gray-100 ${
            isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
          } ${copied ? 'text-green-500' : 'text-gray-400 hover:text-gray-600'}`}
          title="Copy message"
        >
          {copied ? (
            <CheckIcon className="h-5 w-5" />
          ) : (
            <SquaresIcon className="h-5 w-5" />
          )}
        </button>
      </div>
    </div>
  );
}
